import { useMemo, useState, type ReactNode } from "react";
import { DateTime } from "luxon";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { MonthGrid } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useHierarchy } from "@/data/hooks";
import { addDays, customInstant, formatInstant, toIsoUtc } from "@/lib/dates";

/** Explicit reminder instant: day + time in the user's timezone. Shows the resolved time. */
export function ReminderPicker({
  value,
  onChange,
  children,
  align = "start",
}: {
  value: string | null;
  onChange: (iso: string | null) => void;
  children: ReactNode;
  align?: "start" | "end";
}) {
  const { timezone, today } = useHierarchy();
  const initial = value ? DateTime.fromISO(value).setZone(timezone) : null;
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(initial?.toISODate() ?? addDays(today, 1));
  const [time, setTime] = useState(initial?.toFormat("HH:mm") ?? "09:00");
  const at = useMemo(() => customInstant(date, time || "09:00", timezone), [date, time, timezone]);
  const valid = at.isValid && at > DateTime.now();
  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o && value) {
          const v = DateTime.fromISO(value).setZone(timezone);
          setDate(v.toISODate()!);
          setTime(v.toFormat("HH:mm"));
        }
      }}
    >
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align={align} className="w-auto space-y-2 p-2.5" onKeyDown={(e) => e.stopPropagation()}>
        <MonthGrid value={date} today={today} onSelect={setDate} min={today} />
        <div className="flex items-center gap-2">
          <Input aria-label="Reminder time" type="time" value={time} onChange={(e) => setTime(e.target.value)} className="h-7 w-[110px]" />
          <span className="text-[12px] text-muted-foreground">{valid ? formatInstant(at.toISO()!, timezone) : "Pick a future time"}</span>
        </div>
        <div className="flex justify-end gap-1.5">
          {value && (
            <Button
              size="sm"
              variant="quiet"
              onClick={() => {
                onChange(null);
                setOpen(false);
              }}
            >
              Remove
            </Button>
          )}
          <Button
            size="sm"
            variant="default"
            disabled={!valid}
            onClick={() => {
              onChange(toIsoUtc(at));
              setOpen(false);
            }}
          >
            Set reminder
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
